// frontend/src/routes/PrivateRoute.js

import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { clearReducer, decrementLoad, incrementLoad } from "../redux/reducer";
import { getToken } from "../storage/localStorage";
import {
  clearSessionToken,
  fetchCurrentUser,
  sanitizeToken,
  setSessionToken,
} from "../services/api/postgrest";

const bypassAuth = process.env.REACT_APP_BYPASS_AUTH === "true";
const PRIVATE_ROUTE_LOG_PREFIX = "[PrivateRoute]";
const SESSION_VALIDATION_LOG_PREFIX = "[PrivateRoute][Sessao]";

const FullScreenLoader = () => (
  <div className="background-load" style={{ position: "fixed" }}>
    <div
      style={{
        width: "100vw",
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <CircularProgress style={{ color: "#024634" }} />
    </div>
  </div>
);

const useSessionValidation = (tokenFromStore, dispatch) => {
  const [isCheckingSession, setIsCheckingSession] = useState(!bypassAuth);
  const [isSessionValid, setIsSessionValid] = useState(bypassAuth);
  const [hasValidated, setHasValidated] = useState(bypassAuth);

  useEffect(() => {
    if (bypassAuth) {
      return;
    }

    let isActive = true;

    const validateSession = async () => {
      let shouldDecreaseLoad = false;
      const storedToken = tokenFromStore || getToken();
      const sanitizedToken = sanitizeToken(storedToken);

      if (!sanitizedToken) {
        console.warn(
          `${SESSION_VALIDATION_LOG_PREFIX} Token ausente, redirecionando para login`
        );
        clearSessionToken();
        if (isActive) {
          setIsSessionValid(false);
          setHasValidated(true);
          setIsCheckingSession(false);
        }
        return;
      }

      setIsCheckingSession(true);
      dispatch(incrementLoad());
      shouldDecreaseLoad = true;
      setSessionToken(sanitizedToken);

      try {
        await fetchCurrentUser(sanitizedToken);
        if (!isActive) {
          return;
        }

        setIsSessionValid(true);
      } catch (error) {
        if (!isActive) {
          return;
        }

        console.warn(
          `${SESSION_VALIDATION_LOG_PREFIX} Falha ao validar sessão`,
          error
        );
        clearSessionToken();
        dispatch(clearReducer());
        shouldDecreaseLoad = false;
        setIsSessionValid(false);
      } finally {
        if (isActive) {
          setHasValidated(true);
          setIsCheckingSession(false);
          if (shouldDecreaseLoad) {
            dispatch(decrementLoad());
          }
        }
      }
    };

    validateSession();

    return () => {
      isActive = false;
    };
  }, [tokenFromStore, dispatch]);

  return useMemo(
    () => ({
      isCheckingSession,
      isSessionValid,
      hasValidated,
    }),
    [hasValidated, isCheckingSession, isSessionValid]
  );
};

export const PrivateRoute = ({ component: Component, ...rest }) => {
  const dispatch = useDispatch();
  const load = useSelector((state) => state.persistedReducer.load);
  const tokenFromStore = useSelector((state) => state.persistedReducer.token);

  useEffect(() => {
    console.info(`${PRIVATE_ROUTE_LOG_PREFIX} Montando PrivateRoute`, {
      bypassAuth,
    });
  }, []);

  const { isCheckingSession, isSessionValid, hasValidated } =
    useSessionValidation(tokenFromStore, dispatch);

  const shouldShowLoader = Boolean(load) || isCheckingSession;

  if (!hasValidated && !bypassAuth) {
    return <FullScreenLoader />;
  }

  if (!isSessionValid && !bypassAuth) {
    return <Navigate to="/login" replace />;
  }

  return (
    <>
      {shouldShowLoader && <FullScreenLoader />}
      <div
        style={{
          position: "fixed",
          width: "100vw",
          height: "100vh",
          overflow: "hidden",
          backgroundColor: "#ecf0f6",
        }}
      >
        <div className="container-component">
          <Component {...rest} />
        </div>
      </div>
    </>
  );
};
